// ── Dark mode catch-alls (Jade, 2026-10-17) ─────────────────────────────────
// Most of the app paints with var(--c-*), which makeDarkTheme already flips.
// These rules cover what's written straight into components instead: inline
// white / near-white backgrounds and black text (React serialises "#fff" as
// "rgb(255, 255, 255)" in the style attribute, which is what the selectors
// match), native form controls, and the habit tracker's light grey blocks.
// Everything is scoped to html[data-dark], which App.jsx sets only while dark
// mode is showing.
//
// Opt-outs:
//  .keep-white  - white on purpose (switch knobs, the Default preview swatch).
//  .note-light  - a Journal note keeps its own paper colour in dark mode (Jade's
//                 pick), so it gets the light palette back and none of the
//                 catch-alls apply inside it.

const rgb = (hex) => {
  const h = hex.replace("#", "");
  return `rgb(${parseInt(h.slice(0, 2), 16)}, ${parseInt(h.slice(2, 4), 16)}, ${parseInt(h.slice(4, 6), 16)})`;
};
const OUT = ":not(.keep-white):not(.note-light):not(.note-light *)";
const bgSel = (hex) => [
  `html[data-dark] [style*="background: ${rgb(hex)}"]${OUT}`,
  `html[data-dark] [style*="background-color: ${rgb(hex)}"]${OUT}`,
].join(",\n");

// Near-white panel fills used across the app (light pinks, creams, lilacs).
const LIGHT_FILLS = ["#ffffff", "#fff7f9", "#f0e8f0", "#fef8e7", "#fef3e2", "#fcebeb", "#fff0f7", "#eef0ff",
  "#fdf5fa", "#fdfbff", "#fef0f6", "#fafafa", "#f8f8f8", "#f5f5f5", "#fff5f8", "#fff0f0", "#fbf7fd", "#f5edfb"];
// Pale pink border colours written into components.
const LIGHT_BORDERS = ["#f0dcea", "#f0e0ec", "#f5dcea", "#f0d0e8", "#e0d4e8", "#c8f0d0", "#ffd0d0"];
// Habit tracker blocks (ProfileTab / StatisticsTab): missed vs not-due/future.
const BLOCK_MISSED = ["#ddd8ea", "#c8c0d0", "#e8e0f0"];
const BLOCK_EMPTY = ["#e8e4f0", "#eae6ee", "#ddd8e4"];

// The purple-pink dims behind panels and sheets (rgba(60,30,50,..) and friends).
const DIMS = ["60, 30, 50", "40, 20, 35", "40, 20, 40"];
// The pink glow every centred panel carries in light mode. Those elements ARE the
// centred panels (details, add/edit forms), so the same match also lifts them
// onto the raised surface.
const PANEL_GLOWS = ["212, 120, 154", "180, 80, 120"];
// Mauve / pink label text written into components (#a07090, #c07090, #9a70a0,
// #b0a4bc, #4a3040, #4a3050, #3a3040).
const MAUVE_TEXT = ["160, 112, 144", "192, 112, 144", "154, 112, 160", "176, 164, 188"];
const DARK_TEXT = ["74, 48, 64", "74, 48, 80", "58, 48, 64",
  // Plum / near-black inks (#5a3050, #5a2040, #3a2035, #3a2535, #2a1828, #1e1a2e,
  // #4a3070, #1a1a1a, #3a5040): Jade found the Habits History names unreadable.
  "90, 48, 80", "90, 32, 64", "58, 32, 53", "58, 37, 53", "42, 24, 40", "30, 26, 46",
  "74, 48, 112", "26, 26, 26", "58, 80, 64"];
// Saturated mid-dark inks (greens, reds, purples, browns) that read on white but
// sink into a dark card. Lightened toward white, keeping their hue. Only where
// the element has no fill of its own, inside a card: a light chip that keeps its
// colour (the To Do pill, tone badges) keeps its dark text too.
const DEEP_INKS = ["46, 125, 64", "59, 109, 17", "58, 112, 80", "163, 45, 45", "192, 57, 43",
  "124, 79, 212", "160, 64, 200", "122, 80, 192", "122, 80, 16", "122, 80, 112"];
const colorSel = (rgbStr) => [
  `html[data-dark] [style^="color: rgb(${rgbStr})"]${OUT}`,
  `html[data-dark] [style*="; color: rgb(${rgbStr})"]${OUT}`,
].join(",\n");

export const DARK_MODE_CSS = `
html[data-dark] {
  color-scheme: dark;
  /* Settings row lines: a soft accent tone, not near-white (Jade, 2026-10-17). */
  --c-settings-line: color-mix(in srgb, var(--c-primary) 30%, var(--c-bg-card));
}
html[data-dark], html[data-dark] body, html[data-dark] #root { background: var(--c-bg) !important; }

${LIGHT_FILLS.map(bgSel).join(",\n")},
html[data-dark] [style*="background: white"]${OUT},
html[data-dark] [style*="background-color: white"]${OUT} {
  background-color: var(--c-bg-card) !important;
}
${BLOCK_MISSED.map(bgSel).join(",\n")} { background-color: #3a3442 !important; }
${BLOCK_EMPTY.map(bgSel).join(",\n")} { background-color: #2a2730 !important; }

/* Black text written into a component. "; color:" / starts-with keeps it from
   matching background-color or border-color. */
html[data-dark] [style^="color: rgb(0, 0, 0)"]${OUT},
html[data-dark] [style*="; color: rgb(0, 0, 0)"]${OUT},
html[data-dark] [style^="color: black"]${OUT},
html[data-dark] [style*="; color: black"]${OUT} { color: var(--c-text) !important; }
html[data-dark] [style^="color: rgba(0, 0, 0"]${OUT},
html[data-dark] [style*="; color: rgba(0, 0, 0"]${OUT} { color: var(--c-text-soft) !important; }

/* Dims: neutral black instead of the light theme's plum tint (research: dialogs
   on dark sit over a plain scrim; the plum read as "off" under dark panels). */
${DIMS.map(d => `html[data-dark] [style*="background: rgba(${d},"]`).join(",\n")} {
  background-color: rgba(0, 0, 0, 0.58) !important;
}
/* Centred panels: raised neutral surface + a deep shadow to show their edge
   (Jade: shadow, not a white border). */
${PANEL_GLOWS.map(g => `html[data-dark] [style*="box-shadow: rgba(${g},"]${OUT}`).join(",\n")} {
  background-color: var(--c-bg-raised) !important;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.8), 0 4px 14px rgba(0, 0, 0, 0.55) !important;
}
/* Panels inside <Modal> (habit add/edit, habit details, Planner add/edit): the
   same raised surface + shadow. The modal's scroll wrapper clipped the shadow,
   so in dark mode it gets room for it (padding, cancelled by a negative margin,
   and widened by the same amount so the panel keeps its width: Jade saw the
   panels shrink when the padding ate into the 100% width; the 90vh height
   cap gets the same 48px so tall panels keep their height too). */
html[data-dark] .clarity-modal-body { padding: 24px; margin: -24px; width: calc(100% + 48px) !important; max-height: calc(90vh + 48px) !important; }
html[data-dark] .clarity-modal-body > div[style*="box-shadow"]${OUT} {
  background-color: var(--c-bg-raised) !important;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.8), 0 4px 14px rgba(0, 0, 0, 0.55) !important;
}
/* Habits header: sort, Manage habits and Habit Stats buttons (Jade). The sort
   button's open state (accent border) keeps its colour. */
html[data-dark] .hb-head-btn:not([style*="solid var(--c-primary)"]) { border-color: rgba(255, 255, 255, 0.42) !important; }
/* Journal list: search field, sort and folder buttons (Jade). A picked folder
   keeps its accent border and icon. */
html[data-dark] .jr-ctl:not([style*="solid var(--c-primary)"]) { border-color: rgba(255, 255, 255, 0.4) !important; }
html[data-dark] button.jr-ctl:not([style*="solid var(--c-primary)"]), html[data-dark] button.jr-ctl-ico { color: var(--c-text-mid) !important; }
html[data-dark] svg.jr-ctl-ico { stroke: var(--c-text-mid) !important; }
/* Planner notes toolbar (RichNotesEditor): pink / mauve buttons -> white. */
html[data-dark] .rn-tb-btn { color: var(--c-text) !important; }
html[data-dark] .rn-box, html[data-dark] .rn-bar { border-color: var(--c-border-mid) !important; }

/* Desktop Habits dashboard (Jade, 2026-10-18). The light layout paints the To Do
   and Habit Tracker columns as white frosted glass and forces --c-bg-card to pure
   white inside the tracker, so in dark mode the tracker stayed white with pale
   text and the To Do column read as a muddy grey. Dark gets elevation by lighter
   surfaces instead (Material): page --c-bg, columns a step up, cards a step more.
   The 1px hairline marks the column edge, since shadows barely show on dark. */
/* Desktop only: on a phone .habits-main-col is the whole Habits list, which drew
   a box with a hairline round it (Jade, 2026-10-23). */
@media (min-width: 1024px) {
  html[data-dark] .habits-main-col,
  html[data-dark] .habits-graph-col {
    background: color-mix(in srgb, #ffffff 4%, var(--c-bg)) !important;
    -webkit-backdrop-filter: none !important; backdrop-filter: none !important;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06) !important;
  }
}
html[data-dark] .habits-main-col,
html[data-dark] .habits-graph-col {
  --c-bg-card: var(--c-bg-raised); --c-bg-soft: var(--c-bg-raised);
}
html[data-dark] .habits-graph-col > div,
html[data-dark] .habits-cat-col > div { filter: none !important; }
html[data-dark] .habits-graph-col .clarity-card {
  background: var(--c-bg-raised) !important;
  box-shadow: none !important;
}
/* Habit tracker days still to come (Weekly / 4-Week / Yearly): a faint light grey
   that stays visible on the dark card, dimmer than a missed day (Jade, 2026-10-19). */
html[data-dark] .trk-future { background: rgba(255, 255, 255, 0.06) !important; opacity: 1 !important; }
/* 4-Week tracker cards (ProfileTab): white and plum text come from its own <style>
   block, which the inline-style catch-alls can't see (Jade, 2026-10-19). */
html[data-dark] .clarity-habit-month-card {
  background: var(--c-bg-raised) !important;
  box-shadow: none !important;
}
html[data-dark] .clarity-habit-month-card .hm-name { color: var(--c-text) !important; }
html[data-dark] .habits-graph-col .graph-section-frost { background: transparent !important; }
html[data-dark] .habits-graph-col .graph-section-frost .clarity-card-title { color: var(--c-text-mid) !important; }
html[data-dark] .habits-main-col .clarity-row-item { box-shadow: none !important; }
/* The To Do groups' light-mode shadow carries a white inner highlight that drew a
   bright line along each group on dark. */
html[data-dark] .habits-main-col .hb-group { box-shadow: none !important; }
/* Pale pink borders written into components (week nav, Weekly Goal box, ...). */
${LIGHT_BORDERS.map(c => `html[data-dark] [style*="solid ${rgb(c)}"]${OUT}`).join(",\n")} {
  border-color: var(--c-border) !important;
}

${MAUVE_TEXT.map(colorSel).join(",\n")} { color: var(--c-text-mid) !important; }
${DARK_TEXT.map(colorSel).join(",\n")} { color: var(--c-text) !important; }
${DEEP_INKS.map(c => colorSel(c).split(",\n").map(x => x.replace("html[data-dark] ", "html[data-dark] .clarity-card ") + ':not([style*="background"])').join(",\n") + ` { color: color-mix(in srgb, rgb(${c}) 45%, #ffffff) !important; }`).join("\n")}

html[data-dark] input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="color"]),
html[data-dark] textarea,
html[data-dark] select {
  color: var(--c-text);
}
html[data-dark] input::placeholder, html[data-dark] textarea::placeholder { color: var(--c-text-soft); }

/* A Journal note keeps its paper: light palette back inside it. */
html[data-dark] .note-light {
  color-scheme: light;
  --c-bg: #ffffff; --c-bg-soft: #ffffff; --c-bg-card: #ffffff; --c-bg-raised: #ffffff;
  --c-bg-active: color-mix(in srgb, var(--c-primary) 14%, #ffffff);
  --c-text: #2c2433; --c-text-mid: #6a5f72; --c-text-soft: #a399ad;
  --c-text-accent: color-mix(in srgb, var(--c-primary) 62%, #2c2433);
  --c-border: rgba(0,0,0,0.10); --c-border-light: rgba(0,0,0,0.07); --c-border-mid: rgba(0,0,0,0.18);
  --c-shadow: rgba(0,0,0,0.10);
}
html[data-dark] .note-light input, html[data-dark] .note-light textarea { color: var(--c-text); }

/* A note on dark paper (the Black note colour, Jade 2026-10-18): light text in
   ANY app mode, since the paper is the note's own. Listed twice so it outranks
   both the plain palette and dark mode's .note-light block above. */
html .note-light.note-ink-light,
html[data-dark] .note-light.note-ink-light {
  color-scheme: dark;
  --c-text: #f1edf3; --c-text-mid: #cfc8d4; --c-text-soft: #9d95a3;
  --c-text-accent: color-mix(in srgb, var(--c-primary) 55%, #ffffff);
  --c-border: rgba(255,255,255,0.14); --c-border-light: rgba(255,255,255,0.09); --c-border-mid: rgba(255,255,255,0.22);
  /* A text box's own default ink (it isn't --c-text, see JournalTab). */
  --cj-tb-ink: #f1edf3;
}
html .note-ink-light .jrnl-editor hr { border-top-color: rgba(255,255,255,0.25); }
/* The toolbar, its panels and the note menu are white boxes sitting on the
   note: they keep the light palette (dark icons and text). */
html .note-light.note-ink-light [style*="background: rgb(255, 255, 255)"],
html .note-light.note-ink-light [style*="background-color: rgb(255, 255, 255)"],
html .note-light.note-ink-light .clt {
  color-scheme: light;
  --c-text: #2c2433; --c-text-mid: #6a5f72; --c-text-soft: #a399ad;
  --c-text-accent: color-mix(in srgb, var(--c-primary) 62%, #2c2433);
  --c-border: rgba(0,0,0,0.10); --c-border-light: rgba(0,0,0,0.07); --c-border-mid: rgba(0,0,0,0.18);
}
`;
